import React, { useCallback, useEffect, useState } from "react";
import { GiBarn } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { PiMagnifyingGlassPlusBold } from "react-icons/pi";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFuncionario from "../../components/modais/ModalCadastroFuncionario";
import ModalCadastroItemEstoque from "../../components/modais/ModalCadastroItemEstoque";
import ModalCadastroMaquina from "../../components/modais/ModalCadastroMaquina";
import ModalCadastroTalhao from "../../components/modais/ModalCadastroTalhao";
import ModalCadastroTipoAtividade from "../../components/modais/ModalCadastroTipoAtividade";
import { api } from "../../services/api";
import { repositorioAtividade, repositorioTipoAtividade } from "../../sync/repositories/agricultura";
import { repositorioMaquina, repositorioTalhao, repositorioFuncionario } from "../../sync/repositories/fazendas";
import { repositorioCategoriaEstoque, repositorioEstoqueItem, repositorioUnidadeMedida } from "../../sync/repositories/almoxarifado";

// Extrai uma mensagem legível de qualquer formato de erro que a API devolve
// (string, {detail}, {campo: [...]} etc.) — mesmo padrão usado em Financeiro.
const extrairMensagemErro = (error) => {
  const dados = error.response?.data;
  if (!dados) return "Falha na conexão com o servidor.";
  if (typeof dados === "string") return dados;
  if (typeof dados.detail === "string") return dados.detail;
  const mensagens = [];
  const percorrer = (obj) => {
    if (typeof obj === "string") mensagens.push(obj);
    else if (Array.isArray(obj)) obj.forEach(percorrer);
    else if (obj && typeof obj === "object") Object.values(obj).forEach(percorrer);
  };
  percorrer(dados);
  return mensagens.join("\n") || "Erro ao processar a solicitação.";
};

const AJUDA = {
  titulo: "Registro de Atividades",
  descricao:
    "Registre aqui cada operação agrícola realizada em campo (pulverização, plantio, adubação, etc.), com a máquina, o funcionário e os insumos usados. É a partir desse lançamento que o sistema calcula automaticamente os custos de insumos, máquina e mão de obra da operação — e dá baixa nos insumos consumidos no estoque.",
  secoes: [
    {
      titulo: "Dados da Atividade",
      campos: [
        {
          nome: "Data",
          obrigatorio: true,
          explicacao: "Data em que a atividade foi realizada em campo.",
        },
        {
          nome: "Tipo de Atividade",
          obrigatorio: true,
          explicacao: "Qual operação foi executada (ex.: Pulverização, Plantio). Precisa existir no cadastro de Tipos de Atividade — use o botão '+' para criar um novo sem sair desta tela.",
        },
        {
          nome: "Talhão",
          obrigatorio: true,
          explicacao: "Área da fazenda onde a atividade foi executada. A área total do talhão é exibida na listagem para referência.",
        },
        {
          nome: "Máquina",
          obrigatorio: false,
          explicacao: "Máquina/veículo utilizado na atividade. Obrigatória ou não dependendo do Tipo de Atividade selecionado (configurado em Gestão de Tipos de Atividade). O custo por hora cadastrado na máquina é usado para calcular automaticamente o custo de máquina dessa atividade.",
        },
        {
          nome: "Funcionário",
          obrigatorio: false,
          explicacao: "Quem executou a atividade. Obrigatório ou não dependendo do Tipo de Atividade selecionado (configurado em Gestão de Tipos de Atividade). O custo por hora cadastrado no funcionário é usado para calcular automaticamente o custo de mão de obra dessa atividade.",
        },
        {
          nome: "Área Realizada",
          obrigatorio: true,
          explicacao: "Quantos hectares foram efetivamente trabalhados nessa atividade (mínimo 0,01 ha). Pode ser menor que a área total do talhão se o serviço não cobriu tudo.",
        },
        {
          nome: "Km Inicial / Km Final",
          obrigatorio: false,
          explicacao: "Odômetro da máquina no início e no fim do serviço. Preenchendo os dois, o sistema calcula a distância percorrida, exibida no detalhamento de custos.",
        },
        {
          nome: "Descrição / Observações",
          obrigatorio: false,
          explicacao: "Informações adicionais sobre como a atividade foi executada.",
        },
      ],
    },
    {
      titulo: "Insumos Utilizados",
      campos: [
        {
          nome: "Produto",
          obrigatorio: true,
          explicacao: "Item do estoque consumido na atividade (ex.: um herbicida). Clique no campo ou na lupa para abrir a busca de produtos; é necessário ao menos um insumo completo (produto + quantidade + unidade) para salvar a atividade.",
        },
        {
          nome: "Quantidade",
          obrigatorio: true,
          explicacao: "Quanto desse produto foi consumido na atividade. Junto com o custo do produto, define o custo de insumos calculado da atividade.",
        },
        {
          nome: "Unidade",
          obrigatorio: true,
          explicacao: "Unidade de medida da quantidade informada. As opções mudam de acordo com as unidades cadastradas para o produto selecionado.",
        },
      ],
    },
  ],
  dicas: [
    "Os custos de insumos, máquina, mão de obra e o custo total são calculados automaticamente pelo sistema — não são campos digitados. Clique em 'Ver custos' na listagem para ver o detalhamento de cada atividade.",
    "Salvar a atividade dá baixa automaticamente na quantidade dos insumos usados no estoque do Almoxarifado — confira se a quantidade e a unidade estão certas antes de salvar.",
    "É possível adicionar vários insumos na mesma atividade com o botão 'Adicionar Insumo', e remover linhas individualmente com 'Remover'.",
  ],
};

const ITEM_INSUMO_VAZIO = {
  produto: "",
  produto_nome: "",
  quantidade_consumida: "",
  unidade_medida: "",
  unidade_medida_nome: "",
};

export default function GestaoAtividades() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);

  const [atividades, setAtividades] = useState([]);
  const [atividadesFiltradas, setAtividadesFiltradas] = useState([]);

  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  // modal de produto
  const [modalProdutoAberto, setModalProdutoAberto] = useState(false);
  const [indiceItemEditando, setIndiceItemEditando] = useState(null);

  const [buscaProduto, setBuscaProduto] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [produtos, setProdutos] = useState([]);

  // dados auxiliares
  const [tiposAtividade, setTiposAtividade] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [atividadeExpandida, setAtividadeExpandida] = useState(null);
  const [mostrarModalTipoAtividade, setMostrarModalTipoAtividade] = useState(false);
  const [mostrarModalTalhao, setMostrarModalTalhao] = useState(false);
  const [mostrarModalMaquina, setMostrarModalMaquina] = useState(false);
  const [mostrarModalFuncionario, setMostrarModalFuncionario] = useState(false);
  const [mostrarModalItemEstoque, setMostrarModalItemEstoque] = useState(false);
  const toggleCustos = (atividadeId) => {
    setAtividadeExpandida((prev) =>
      prev === atividadeId ? null : atividadeId,
    );
  };

  const [filtros, setFiltros] = useState({
    busca: "",
    data_inicio: "",
    data_fim: "",
  });

  const [formData, setFormData] = useState({
    data: "",
    tipo_atividade: "",
    talhao: "",
    maquina: "",
    funcionario: "",
    area_realizada_hectares: "",
    km_inicial: "",
    km_final: "",
    descricao: "",
  });

  const [itensInsumo, setItensInsumo] = useState([{ ...ITEM_INSUMO_VAZIO }]);

  // Exigências de máquina/funcionário/insumo variam por Tipo de Atividade
  // (campo cadastrado em Gestão de Tipos de Atividade) — enquanto o tipo não
  // é selecionado, assume o comportamento anterior (máquina e funcionário
  // obrigatórios, insumo opcional).
  const tipoSelecionado = tiposAtividade.find(
    (t) => String(t.id) === String(formData.tipo_atividade),
  );
  const maquinaExigida = tipoSelecionado ? !!tipoSelecionado.exige_maquina : true;
  const funcionarioExigido = tipoSelecionado ? !!tipoSelecionado.exige_funcionario : true;
  const insumoExigido = tipoSelecionado ? !!tipoSelecionado.exige_insumo : false;

  const decodeHtml = (texto) => {
    if (!texto) return "";
    const txt = document.createElement("textarea");
    txt.innerHTML = texto;
    return txt.value;
  };

  // =========================
  // CARREGAMENTOS
  // =========================
  const carregarDados = useCallback(async () => {
    try {
      const [
        tiposRes,
        talhoesRes,
        maquinasRes,
        funcionariosRes,
        insumosRes,
        unidadesRes,
      ] = await Promise.all([
        repositorioTipoAtividade.listar(),
        repositorioTalhao.listar(),
        repositorioMaquina.listar(),
        repositorioFuncionario.listar(),
        repositorioEstoqueItem.listar(),
        repositorioUnidadeMedida.listar(),
      ]);

      setTiposAtividade(tiposRes.data?.results || tiposRes.data || []);
      setTalhoes(talhoesRes.data?.results || talhoesRes.data || []);
      setMaquinas(maquinasRes.data?.results || maquinasRes.data || []);
      setFuncionarios(
        funcionariosRes.data?.results || funcionariosRes.data || [],
      );
      setInsumos(insumosRes.data?.results || insumosRes.data || []);
      setUnidadesMedida(unidadesRes.data?.results || unidadesRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      alert("Erro ao carregar dados auxiliares da tela.");
    }
  }, []);

  const carregarCategorias = useCallback(async () => {
    try {
      const response = await repositorioCategoriaEstoque.listar();
      const data = response.data?.results || response.data || [];
      setCategorias(data);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setCategorias([]);
    }
  }, []);

  const carregarProdutos = useCallback(async () => {
    try {
      const response = await repositorioEstoqueItem.listar();
      const data = response.data?.results || response.data || [];
      setProdutos(data);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      setProdutos([]);
    }
  }, []);

  const carregarAtividades = useCallback(async () => {
    try {
      setCarregandoLista(true);

      const response = await repositorioAtividade.listar();
      const dados = response.data?.results || response.data || [];

      const atividadesNormalizadas = dados.map((atividade) => ({
        ...atividade,
        tipo_atividade_nome:
          atividade.tipo_atividade_nome || atividade.tipo_atividade?.nome || "",
        talhao_nome_codigo:
          atividade.talhao_nome_codigo || atividade.talhao?.nome_codigo || "",
        maquina_nome: atividade.maquina_nome || atividade.maquina?.modelo || "",
        funcionario_nome:
          atividade.funcionario_nome || atividade.funcionario?.nome || "",
        distancia_percorrida_km: atividade.distancia_percorrida_km || 0,
        horas_trabalhadas:
          atividade.horas_trabalhadas_calculadas ||
          atividade.horas_trabalhadas ||
          0,
        custo_insumos: atividade.custo_insumos || 0,
        custo_maquina: atividade.custo_maquina || 0,
        custo_mao_de_obra: atividade.custo_mao_de_obra || 0,
        custo_total_calculado:
          atividade.custo_total_calculado || atividade.custo_total || 0,
        area_talhao_total: atividade.area_talhao_total || 0,
      }));

      setAtividades(atividadesNormalizadas);
      setAtividadesFiltradas(atividadesNormalizadas);
    } catch (error) {
      console.error("Erro ao carregar atividades:", error);
      alert("Erro ao carregar lista de atividades.");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarAtividades();
    carregarDados();
    carregarCategorias();
    carregarProdutos();
  }, [carregarAtividades, carregarDados, carregarCategorias, carregarProdutos]);

  // =========================
  // FORM / CAMPOS
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItensInsumo((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const getUnidadesDisponiveis = (produtoId) => {
    const produto = insumos.find((p) => String(p.id) === String(produtoId));

    if (
      produto &&
      Array.isArray(produto.unidades_medida) &&
      produto.unidades_medida.length > 0
    ) {
      return produto.unidades_medida;
    }

    return unidadesMedida;
  };

  const handleUnidadeChange = (index, unidadeId) => {
    const unidade = unidadesMedida.find(
      (u) => String(u.id) === String(unidadeId),
    );

    setItensInsumo((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              unidade_medida: unidadeId,
              unidade_medida_nome:
                unidade?.nome || unidade?.descricao || unidade?.sigla || "",
            }
          : item,
      ),
    );
  };

  const addInsumo = () => {
    setItensInsumo((prev) => [...prev, { ...ITEM_INSUMO_VAZIO }]);
  };

  const removeInsumo = (index) => {
    setItensInsumo((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : [{ ...ITEM_INSUMO_VAZIO }];
    });
  };

  // =========================
  // MODAL PRODUTO - CORRIGIDO
  // =========================
  const abrirModalProduto = (index) => {
    setIndiceItemEditando(index);
    setBuscaProduto("");
    setFiltroCategoria("");
    setModalProdutoAberto(true);
    // Previne scroll do body
    document.body.style.overflow = "hidden";
  };

  // Cadastro rápido de produto — reaproveita indiceItemEditando/selecionarProduto
  // do fluxo de busca (abrirModalProduto), só troca o modal que abre.
  const abrirModalNovoProduto = (index) => {
    setIndiceItemEditando(index);
    setMostrarModalItemEstoque(true);
  };

  const fecharModalProduto = () => {
    setModalProdutoAberto(false);
    setIndiceItemEditando(null);
    setBuscaProduto("");
    setFiltroCategoria("");
    // Restaura scroll do body
    document.body.style.overflow = "unset";
  };

  const selecionarProduto = (produto) => {
    if (indiceItemEditando === null) return;

    const itensAtualizados = [...itensInsumo];

    itensAtualizados[indiceItemEditando] = {
      ...itensAtualizados[indiceItemEditando],
      produto: produto.id,
      produto_nome: produto.nome || produto.nome_produto || "",
      unidade_medida:
        produto?.unidade_medida?.id || produto?.unidade_medida || "",
      unidade_medida_nome:
        produto?.unidade_medida?.nome ||
        produto?.unidade_medida?.descricao ||
        produto?.unidade_medida_nome ||
        "",
    };

    setItensInsumo(itensAtualizados);
    fecharModalProduto();
  };

  const produtosFiltrados = (produtos || []).filter((produto) => {
    const nome = (produto?.nome || produto?.nome_produto || "").toLowerCase();
    const codigo = (
      produto?.codigo ||
      produto?.codigo_interno ||
      produto?.sku ||
      ""
    ).toLowerCase();

    const categoriaNome = (
      produto?.categoria_nome ||
      produto?.categoria?.nome ||
      ""
    ).toLowerCase();

    const busca = buscaProduto.toLowerCase().trim();

    const passouBusca =
      !busca ||
      nome.includes(busca) ||
      codigo.includes(busca) ||
      categoriaNome.includes(busca);

    const passouCategoria =
      !filtroCategoria ||
      String(produto?.categoria) === String(filtroCategoria) ||
      String(produto?.categoria_id) === String(filtroCategoria);

    return passouBusca && passouCategoria;
  });

  // =========================
  // VALIDAÇÃO / SUBMIT
  // =========================
  const validarFormulario = () => {
    if (!formData.data) {
      alert("Informe a data da atividade.");
      return false;
    }
    if (!formData.tipo_atividade) {
      alert("Selecione o tipo de atividade.");
      return false;
    }
    if (!formData.talhao) {
      alert("Selecione o talhão.");
      return false;
    }
    if (maquinaExigida && !formData.maquina) {
      alert(`Selecione a máquina (obrigatória para "${tipoSelecionado?.nome}").`);
      return false;
    }
    if (funcionarioExigido && !formData.funcionario) {
      alert(`Selecione o funcionário (obrigatório para "${tipoSelecionado?.nome}").`);
      return false;
    }
    if (
      !formData.area_realizada_hectares ||
      parseFloat(formData.area_realizada_hectares) < 0.01
    ) {
      alert("Informe uma área realizada válida (mínimo 0,01 hectares).");
      return false;
    }
    if (
      insumoExigido &&
      itensInsumo.filter((i) => i.produto && i.quantidade_consumida && i.unidade_medida).length === 0
    ) {
      alert(`Adicione ao menos um insumo (obrigatório para "${tipoSelecionado?.nome}").`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    const itensPayload = itensInsumo
      .filter((i) => i.produto && i.quantidade_consumida && i.unidade_medida)
      .map((i) => ({
        ...(i.id ? { id: i.id } : {}),
        produto: Number(i.produto),
        quantidade_consumida: Number(i.quantidade_consumida),
        unidade_medida: Number(i.unidade_medida),
      }));

    // Só pergunta se a baixa automática está configurada pra confirmar antes
    // de dar saída no estoque — na maioria das fazendas isso fica desligado
    // e o lançamento segue direto, sem interromper o fluxo.
    if (itensPayload.length > 0) {
      try {
        const resp = await api.get("/configuracoes/sistema/", { params: { modulo: "almoxarifado" } });
        const configs = resp.data?.configuracoes || [];
        const baixaAutomatica = configs.find((c) => c.chave === "ESTOQUE_BAIXA_AUTOMATICA")?.valor_efetivo;
        const confirmarBaixa = configs.find((c) => c.chave === "ESTOQUE_CONFIRMAR_BAIXA_AUTOMATICA")?.valor_efetivo;
        if (baixaAutomatica && confirmarBaixa) {
          if (!window.confirm("Esta atividade vai dar baixa automática nos insumos informados no estoque. Confirmar?")) {
            return;
          }
        }
      } catch (error) {
        console.error("Erro ao verificar configuração de baixa automática:", error);
      }
    }

    let justificativa;
    if (editando) {
      justificativa = window.prompt("Justificativa da alteração (deixe em branco se não for exigida):") || "";
    }

    setLoading(true);

    const payload = {
      data: formData.data,
      tipo_atividade: Number(formData.tipo_atividade),
      talhao: Number(formData.talhao),
      maquina: formData.maquina ? Number(formData.maquina) : null,
      funcionario: formData.funcionario ? Number(formData.funcionario) : null,
      area_realizada_hectares: Number(formData.area_realizada_hectares),
      km_inicial: formData.km_inicial ? Number(formData.km_inicial) : null,
      km_final: formData.km_final ? Number(formData.km_final) : null,
      descricao: formData.descricao || "",
      itens_insumo: itensPayload,
      ...(editando ? { justificativa } : {}),
    };

    try {
      if (editando) {
        await repositorioAtividade.atualizar(editandoId, payload);
        alert("Atividade atualizada com sucesso!");
      } else {
        await repositorioAtividade.criar(payload);
        alert("Atividade cadastrada com sucesso!");
      }

      handleCancel();
      carregarAtividades();
    } catch (error) {
      console.error("Erro ao salvar atividade:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CRUD / EDIÇÃO
  // =========================
  const handleEditar = (atividade) => {
    setFormData({
      data: atividade.data || "",
      tipo_atividade:
        atividade.tipo_atividade?.id || atividade.tipo_atividade || "",
      talhao: atividade.talhao?.id || atividade.talhao || "",
      maquina: atividade.maquina?.id || atividade.maquina || "",
      funcionario: atividade.funcionario?.id || atividade.funcionario || "",
      area_realizada_hectares: atividade.area_realizada_hectares || "",
      km_inicial: atividade.km_inicial || "",
      km_final: atividade.km_final || "",
      descricao: atividade.descricao || "",
    });

    if (atividade.itens_insumo && atividade.itens_insumo.length > 0) {
      setItensInsumo(
        atividade.itens_insumo.map((item) => ({
          id: item.id,
          produto: item.produto?.id || item.produto || "",
          produto_nome: item.produto_nome || item.produto?.nome || "",
          quantidade_consumida: item.quantidade_consumida || "",
          unidade_medida: item.unidade_medida?.id || item.unidade_medida || "",
          unidade_medida_nome:
            item.unidade_medida?.nome ||
            item.unidade_medida?.descricao ||
            item.unidade_medida_nome ||
            "",
        })),
      );
    } else {
      setItensInsumo([{ ...ITEM_INSUMO_VAZIO }]);
    }

    setEditando(true);
    setEditandoId(atividade.id);
    setMostrarFormulario(true);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleNovaAtividade = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      data: "",
      tipo_atividade: "",
      talhao: "",
      maquina: "",
      funcionario: "",
      area_realizada_hectares: "",
      km_inicial: "",
      km_final: "",
      descricao: "",
    });

    setItensInsumo([{ ...ITEM_INSUMO_VAZIO }]);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    fecharModalProduto();
  };

  const handleExcluir = async (id) => {
    if (
      !window.confirm(
        "Tem certeza que deseja excluir esta atividade? Esta ação não pode ser desfeita.",
      )
    ) {
      return;
    }

    const justificativa = window.prompt("Justificativa da exclusão (deixe em branco se não for exigida):") || "";

    try {
      if (navigator.onLine) {
        await api.delete(`/agricultura/atividades/${id}/`, { data: { justificativa } });
      } else {
        await repositorioAtividade.excluir(id);
      }
      alert("Atividade excluída com sucesso!");
      carregarAtividades();

      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert(`❌ ${extrairMensagemErro(error)}`);
    }
  };

  // =========================
  // FILTROS LISTAGEM
  // =========================
  const aplicarFiltros = () => {
    let filtrados = [...atividades];

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();

      filtrados = filtrados.filter(
        (a) =>
          a.tipo_atividade_nome?.toLowerCase().includes(buscaLower) ||
          a.talhao_nome_codigo?.toLowerCase().includes(buscaLower) ||
          a.maquina_nome?.toLowerCase().includes(buscaLower) ||
          a.funcionario_nome?.toLowerCase().includes(buscaLower),
      );
    }

    if (filtros.data_inicio) {
      filtrados = filtrados.filter(
        (a) => new Date(a.data) >= new Date(filtros.data_inicio),
      );
    }

    if (filtros.data_fim) {
      filtrados = filtrados.filter(
        (a) => new Date(a.data) <= new Date(filtros.data_fim),
      );
    }

    setAtividadesFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", data_inicio: "", data_fim: "" });
    setAtividadesFiltradas(atividades);
  };

  // =========================
  // FORMATADORES
  // =========================
  const formatarData = (data) => {
    if (!data) return "—";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const formatarArea = (area) => {
    if (area === null || area === undefined || area === "") return "—";
    return `${Number(area).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ha`;
  };

  const formatarMoeda = (valor) => {
    if (valor === null || valor === undefined || valor === "") return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(valor));
  };

  const formatarHoras = (horas) => {
    if (horas === null || horas === undefined || horas === "") return "—";

    return `${Number(horas).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} h`;
  };

  const formatarKm = (km) => {
    if (km === null || km === undefined || km === "") return "—";

    return `${Number(km).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} km`;
  };

  // =========================
  // COMPONENTE MODAL - CORRIGIDO COM Z-INDEX MAIOR
  // =========================
  const ModalProduto = () => {
    if (!modalProdutoAberto) return null;

    return (
      <>
        {/* Overlay com z-index altíssimo */}
        <div
          className="fixed inset-0 z-[99999]"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
          }}
          onClick={fecharModalProduto}
        />

        {/* Modal com z-index ainda maior que o overlay */}
        <div
          className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            pointerEvents: "none", // Permite cliques no modal
          }}
        >
          <div
            className="relative flex w-full max-w-4xl lg:max-w-6xl flex-col bg-white rounded-2xl shadow-2xl"
            style={{
              maxHeight: "90vh",
              margin: "auto",
              pointerEvents: "auto", // Habilita cliques no modal
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                  Selecionar Produto
                </h2>
                <p className="text-xs sm:text-sm text-gray-500">
                  Clique em um produto para selecionar
                </p>
              </div>

              <button
                onClick={fecharModalProduto}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 self-start sm:self-auto"
                type="button"
              >
                ✕ Fechar
              </button>
            </div>

            {/* BUSCA / FILTROS */}
            <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
                <input
                  type="text"
                  placeholder="Buscar produto por nome, código ou categoria..."
                  value={buscaProduto}
                  onChange={(e) => setBuscaProduto(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 sm:py-3 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                />

                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 sm:py-3 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="">Todas as categorias</option>
                  {categorias?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* CONTEÚDO */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5">
              {produtosFiltrados.length === 0 ? (
                <div className="flex h-full min-h-[200px] items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 text-center p-4">
                  <div>
                    <p className="text-base font-semibold text-gray-700">
                      Nenhum produto encontrado
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Tente buscar por outro nome ou ajustar os filtros.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {produtosFiltrados.map((produto) => {
                    const nome = decodeHtml(
                      produto.nome ||
                        produto.nome_produto ||
                        "Produto sem nome",
                    );

                    const codigo =
                      produto.codigo ||
                      produto.codigo_interno ||
                      produto.sku ||
                      "N/A";

                    const unidade =
                      produto.unidade_medida?.descricao ||
                      produto.unidade_medida?.nome ||
                      produto.unidade_medida?.sigla ||
                      produto.unidade ||
                      "N/A";

                    const estoqueAtual =
                      produto.quantidade_atual ??
                      produto.saldo_atual ??
                      produto.estoque_atual ??
                      null;

                    return (
                      <button
                        key={produto.id}
                        type="button"
                        onClick={() => selecionarProduto(produto)}
                        className="group flex min-h-[150px] sm:min-h-[170px] flex-col rounded-2xl border border-gray-200 bg-white p-3 sm:p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-500 hover:shadow-md"
                      >
                        <div className="flex-1">
                          <h3 className="line-clamp-2 break-words text-sm sm:text-base font-semibold leading-6 text-gray-800">
                            {nome}
                          </h3>

                          <div className="mt-2 sm:mt-3 space-y-1 text-xs sm:text-sm text-gray-600">
                            <p>
                              <span className="font-medium text-gray-700">
                                Código:
                              </span>{" "}
                              {codigo}
                            </p>
                            <p>
                              <span className="font-medium text-gray-700">
                                Unidade padrão:
                              </span>{" "}
                              {unidade}
                            </p>

                            {estoqueAtual !== null && (
                              <p>
                                <span className="font-medium text-gray-700">
                                  Estoque atual:
                                </span>{" "}
                                {estoqueAtual}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 sm:mt-4 flex items-center justify-between">
                          <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                            Selecionar
                          </span>

                          <span className="text-xs text-gray-400 transition group-hover:text-emerald-600 hidden sm:inline">
                            Clique para adicionar
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="flex justify-end border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
              <button
                type="button"
                onClick={fecharModalProduto}
                className="rounded-xl bg-gray-700 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 font-medium">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-2">
            <h2 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold text-gray-800">
              Gestão de Atividades{" "}
              <GiBarn size={28} className="hidden sm:inline-block" />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>

          {!mostrarFormulario && (
            <button
              onClick={handleNovaAtividade}
              className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 sm:px-6 py-2.5 text-white shadow-md transition-colors hover:bg-blue-700 w-full sm:w-auto"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Nova Atividade
            </button>
          )}
        </div>

        {/* FORMULÁRIO */}
        {mostrarFormulario && (
          <div className="mb-8 rounded-lg border border-gray-200 bg-white p-4 sm:p-6 shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                {editando ? "Editar Atividade" : "Cadastrar Nova Atividade"}
              </h3>

              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700 self-start sm:self-auto"
                title="Fechar formulário"
                type="button"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4 sm:space-y-6">
                {/* Linha 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Data *
                    </label>
                    <input
                      type="date"
                      name="data"
                      value={formData.data}
                      onChange={handleChange}
                      className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Tipo de Atividade *
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        name="tipo_atividade"
                        value={formData.tipo_atividade}
                        onChange={handleChange}
                        className="flex-1 rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                        required
                      >
                        <option value="">Selecione...</option>
                        {tiposAtividade.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalTipoAtividade(true)}
                        title="Cadastrar novo tipo"
                        className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                      >
                        <LuCopyPlus
                          size={24}
                          className="text-green-600 hover:text-green-800"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Talhão *
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        name="talhao"
                        value={formData.talhao}
                        onChange={handleChange}
                        className="flex-1 rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                        required
                      >
                        <option value="">Selecione...</option>
                        {talhoes.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome_codigo} ({t.area_hectares} ha)
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalTalhao(true)}
                        title="Cadastrar novo talhão"
                        className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                      >
                        <LuCopyPlus
                          size={24}
                          className="text-green-600 hover:text-green-800"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Máquina {maquinaExigida ? "*" : "(opcional para este tipo)"}
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        name="maquina"
                        value={formData.maquina}
                        onChange={handleChange}
                        className="flex-1 rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                        required={maquinaExigida}
                      >
                        <option value="">Selecione...</option>
                        {maquinas.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.veiculos} - {m.modelo} (R$ {m.custo_hora}/h)
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalMaquina(true)}
                        title="Cadastrar nova máquina"
                        className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                      >
                        <LuCopyPlus
                          size={24}
                          className="text-green-600 hover:text-green-800"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Funcionário {funcionarioExigido ? "*" : "(opcional para este tipo)"}
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        name="funcionario"
                        value={formData.funcionario}
                        onChange={handleChange}
                        className="flex-1 rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                        required={funcionarioExigido}
                      >
                        <option value="">Selecione...</option>
                        {funcionarios.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.nome} (R$ {f.custo_hora}/h)
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalFuncionario(true)}
                        title="Cadastrar novo funcionário"
                        className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                      >
                        <LuCopyPlus
                          size={24}
                          className="text-green-600 hover:text-green-800"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Linha 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Área Realizada *
                    </label>
                    <div className="flex items-center rounded border border-green-500 overflow-hidden">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        name="area_realizada_hectares"
                        value={formData.area_realizada_hectares}
                        onChange={handleChange}
                        className="w-full p-2.5 focus:ring-2 focus:ring-green-300"
                        placeholder="0,01"
                        required
                      />
                      <span className="bg-gray-100 px-3 py-2.5 text-gray-700 flex-shrink-0">
                        ha
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Km Inicial
                    </label>
                    <div className="flex items-center rounded border border-green-500 overflow-hidden">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        name="km_inicial"
                        value={formData.km_inicial}
                        onChange={handleChange}
                        className="w-full p-2.5 focus:ring-2 focus:ring-green-300"
                        placeholder="0"
                      />
                      <span className="bg-gray-100 px-3 py-2.5 text-gray-700 flex-shrink-0">
                        km
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-gray-700">
                      Km Final
                    </label>
                    <div className="flex items-center rounded border border-green-500 overflow-hidden">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        name="km_final"
                        value={formData.km_final}
                        onChange={handleChange}
                        className="w-full p-2.5 focus:ring-2 focus:ring-green-300"
                        placeholder="0"
                      />
                      <span className="bg-gray-100 px-3 py-2.5 text-gray-700 flex-shrink-0">
                        km
                      </span>
                    </div>
                  </div>
                </div>

                {/* descrição */}
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-gray-700">
                    Descrição / Observações
                  </label>
                  <textarea
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    rows={3}
                    className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                    placeholder="Informações adicionais sobre a atividade..."
                  />
                </div>

                {/* INSUMOS */}
                <div className="border-t pt-4">
                  <h3 className="mb-4 text-lg font-semibold text-gray-800">
                    Insumos Utilizados {insumoExigido && "*"}
                  </h3>

                  {itensInsumo.map((item, index) => (
                    <div
                      key={index}
                      className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium text-gray-700">
                            Produto
                          </label>

                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={item.produto_nome}
                              readOnly
                              onClick={() => abrirModalProduto(index)}
                              placeholder="Clique para selecionar"
                              className="flex-1 cursor-pointer rounded border border-green-500 bg-gray-50 p-2.5 focus:ring-2 focus:ring-green-300 text-sm"
                            />

                            <button
                              type="button"
                              onClick={() => abrirModalProduto(index)}
                              title="Pesquisar produto"
                              className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                            >
                              <PiMagnifyingGlassPlusBold
                                size={22}
                                className="text-green-600 hover:text-green-800"
                              />
                            </button>

                            <button
                              type="button"
                              onClick={() => abrirModalNovoProduto(index)}
                              title="Cadastrar novo produto"
                              className="flex-shrink-0 rounded p-2 hover:bg-gray-200"
                            >
                              <LuCopyPlus
                                size={22}
                                className="text-green-600 hover:text-green-800"
                              />
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium text-gray-700">
                            Quantidade
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.quantidade_consumida}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantidade_consumida",
                                e.target.value,
                              )
                            }
                            className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                            placeholder="0,00"
                          />
                        </div>

                        <div className="flex flex-col">
                          <label className="mb-1 text-sm font-medium text-gray-700">
                            Unidade
                          </label>
                          <select
                            value={item.unidade_medida}
                            onChange={(e) =>
                              handleUnidadeChange(index, e.target.value)
                            }
                            className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                            disabled={!item.produto}
                          >
                            <option value="">Selecione</option>
                            {item.produto &&
                              getUnidadesDisponiveis(item.produto).map(
                                (unidade) => (
                                  <option key={unidade.id} value={unidade.id}>
                                    {unidade.nome || unidade.descricao}{" "}
                                    {unidade.sigla ? `(${unidade.sigla})` : ""}
                                  </option>
                                ),
                              )}
                          </select>
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeInsumo(index)}
                            className="w-full rounded bg-red-600 px-4 py-2.5 text-white transition-colors hover:bg-red-700"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addInsumo}
                    className="mt-2 flex items-center justify-center gap-2 rounded bg-green-600 px-4 py-2.5 text-white transition-colors hover:bg-green-700 w-full sm:w-auto"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Adicionar Insumo
                  </button>
                </div>

                {/* botões */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-lg bg-blue-600 px-6 py-2.5 text-white transition-colors hover:bg-blue-700 disabled:bg-blue-300 w-full sm:w-auto"
                  >
                    {loading
                      ? "Salvando..."
                      : editando
                        ? "Atualizar"
                        : "Cadastrar"}
                  </button>

                  <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-lg bg-red-600 px-6 py-2.5 text-white transition-colors hover:bg-red-700 w-full sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* MODAL DE PRODUTO - COM Z-INDEX CORRIGIDO */}
        <ModalProduto />

        {/* LISTAGEM */}
        {!mostrarFormulario && (
          <>
            {/* FILTROS */}
            <div className="mb-6 sm:mb-8 rounded-lg border border-gray-200 bg-white p-4 sm:p-6 shadow-md">
              <h3 className="mb-4 flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-700">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filtros de Busca
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-gray-700">
                    Buscar
                  </label>
                  <input
                    type="text"
                    placeholder="Atividade, talhão ou máquina..."
                    value={filtros.busca}
                    onChange={(e) =>
                      setFiltros({ ...filtros, busca: e.target.value })
                    }
                    className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-gray-700">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) =>
                      setFiltros({ ...filtros, data_inicio: e.target.value })
                    }
                    className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-gray-700">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) =>
                      setFiltros({ ...filtros, data_fim: e.target.value })
                    }
                    className="rounded border border-green-500 p-2.5 focus:ring-2 focus:ring-green-300 w-full"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={aplicarFiltros}
                  className="rounded bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="rounded bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* TABELA */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
              <div className="border-b border-gray-200 bg-gray-50 p-3 sm:p-4">
                <p className="text-sm sm:text-base text-gray-600">
                  {atividadesFiltradas.length} atividade(s) encontrada(s)
                </p>
              </div>

              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">
                  Carregando...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
                          Código
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
                          Data
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
                          Atividade
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden lg:table-cell">
                          Talhão
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden md:table-cell">
                          Máquina
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden md:table-cell">
                          Funcionário
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700 hidden xl:table-cell">
                          Custo Insumos
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
                          Custo Total
                        </th>
                        <th className="p-2 sm:p-3 text-left text-xs sm:text-sm font-semibold text-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {atividadesFiltradas.map((atividade) => (
                        <React.Fragment key={atividade.id}>
                          <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">
                              {atividade.id}
                            </td>

                            <td className="p-3 text-sm">
                              {formatarData(atividade.data)}
                            </td>

                            <td className="p-3 text-sm font-medium">
                              {atividade.tipo_atividade_nome || "-"}
                            </td>

                            <td className="p-3 text-sm">
                              <div>
                                <div>{atividade.talhao_nome_codigo || "-"}</div>
                                <div className="text-xs text-gray-500">
                                  Área total:{" "}
                                  {formatarArea(atividade.area_talhao_total)}
                                </div>
                              </div>
                            </td>

                            <td className="p-3 text-sm">
                              <div>
                                <div>{atividade.maquina_nome || "-"}</div>
                                <div className="text-xs text-gray-500">
                                  Custo/h:{" "}
                                  {formatarMoeda(atividade.maquina?.custo_hora)}
                                </div>
                              </div>
                            </td>

                            <td className="p-3 text-sm">
                              <div>
                                <div>{atividade.funcionario_nome || "-"}</div>
                                <div className="text-xs text-gray-500">
                                  Custo/h:{" "}
                                  {formatarMoeda(
                                    atividade.funcionario?.custo_hora,
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* COLUNA CUSTOS */}
                            <td className="p-3 text-sm">
                              <div className="flex flex-col gap-2">
                                <div className="text-blue-600 font-bold text-base">
                                  {formatarMoeda(
                                    atividade.custo_total_calculado ||
                                      atividade.custo_total,
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => toggleCustos(atividade.id)}
                                  className="w-fit text-xs font-medium text-green-700 hover:text-green-900 hover:underline"
                                >
                                  {atividadeExpandida === atividade.id
                                    ? "Ocultar custos"
                                    : "Ver custos"}
                                </button>
                              </div>
                            </td>

                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditar(atividade)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(atividade.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* LINHA EXPANDIDA DE CUSTOS */}
                          {atividadeExpandida === atividade.id && (
                            <tr className="bg-green-50 border-b border-gray-200">
                              <td colSpan={8} className="px-4 py-4">
                                <div className="rounded-xl border border-green-200 bg-white p-4 shadow-sm">
                                  <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-sm md:text-base font-semibold text-gray-800">
                                      Detalhamento de custos da atividade #
                                      {atividade.id}
                                    </h4>

                                    <button
                                      type="button"
                                      onClick={() => toggleCustos(atividade.id)}
                                      className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                      Fechar
                                    </button>
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Custo de insumos
                                      </p>
                                      <p className="text-lg font-bold text-green-700">
                                        {formatarMoeda(atividade.custo_insumos)}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Custo da máquina
                                      </p>
                                      <p className="text-lg font-bold text-amber-700">
                                        {formatarMoeda(atividade.custo_maquina)}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Custo mão de obra
                                      </p>
                                      <p className="text-lg font-bold text-purple-700">
                                        {formatarMoeda(
                                          atividade.custo_mao_de_obra,
                                        )}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Horas trabalhadas
                                      </p>
                                      <p className="text-lg font-bold text-gray-800">
                                        {formatarHoras(
                                          atividade.horas_trabalhadas,
                                        )}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3 bg-blue-50 border-blue-200">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Custo total
                                      </p>
                                      <p className="text-lg font-bold text-blue-700">
                                        {formatarMoeda(
                                          atividade.custo_total_calculado ||
                                            atividade.custo_total,
                                        )}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="rounded-lg border border-gray-200 p-3">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Distância percorrida
                                      </p>
                                      <p className="font-semibold text-gray-800">
                                        {formatarKm(
                                          atividade.distancia_percorrida_km,
                                        )}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Área realizada
                                      </p>
                                      <p className="font-semibold text-gray-800">
                                        {formatarArea(
                                          atividade.area_realizada_hectares,
                                        )}
                                      </p>
                                    </div>

                                    <div className="rounded-lg border border-gray-200 p-3">
                                      <p className="text-xs text-gray-500 mb-1">
                                        Talhão
                                      </p>
                                      <p className="font-semibold text-gray-800">
                                        {atividade.talhao_nome_codigo || "-"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {atividadesFiltradas.length === 0 && (
                        <tr>
                          <td
                            colSpan="9"
                            className="p-8 text-center text-gray-500"
                          >
                            {filtros.busca ||
                            filtros.data_inicio ||
                            filtros.data_fim
                              ? "Nenhuma atividade encontrada com os filtros selecionados."
                              : "Nenhuma atividade cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroTipoAtividade
        isOpen={mostrarModalTipoAtividade}
        onClose={() => setMostrarModalTipoAtividade(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setFormData((prev) => ({ ...prev, tipo_atividade: novo.id }));
        }}
      />
      <ModalCadastroTalhao
        isOpen={mostrarModalTalhao}
        onClose={() => setMostrarModalTalhao(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setFormData((prev) => ({ ...prev, talhao: novo.id }));
        }}
      />
      <ModalCadastroMaquina
        isOpen={mostrarModalMaquina}
        onClose={() => setMostrarModalMaquina(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setFormData((prev) => ({ ...prev, maquina: novo.id }));
        }}
      />
      <ModalCadastroFuncionario
        isOpen={mostrarModalFuncionario}
        onClose={() => setMostrarModalFuncionario(false)}
        onSuccess={async (novo) => {
          await carregarDados();
          setFormData((prev) => ({ ...prev, funcionario: novo.id }));
        }}
      />
      <ModalCadastroItemEstoque
        isOpen={mostrarModalItemEstoque}
        onClose={() => setMostrarModalItemEstoque(false)}
        unidadesMedida={unidadesMedida}
        onSuccess={async (novo) => {
          await carregarProdutos();
          selecionarProduto(novo);
          setMostrarModalItemEstoque(false);
        }}
      />
    </Layout>
  );
}
